import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  MdAccountBalanceWallet,
  MdCalendarMonth,
  MdClear,
  MdFilterList,
  MdPayments,
  MdRefresh,
  MdTrendingUp,
} from "react-icons/md";
import { toast } from "react-toastify";

import { useAppContext } from "../contexts/AppContextProvider";
import { CustomTable } from "./index.js";
import { camelCaseToCapitalCase } from "../utils/inputValidations.js";
import {
  isInvalidDateRange,
  parseFilterDate,
} from "../utils/partnerFilterUtils.js";
import {
  convertToIst,
  isoTimestampToReadable,
} from "../utils/timeCalculator.js";

const DEFAULT_FILTERS = {
  dateFrom: "",
  dateTo: "",
  currency: "",
  corpName: "",
  minAmount: "",
  maxAmount: "",
};

const filterInputSx = {
  "& .MuiInputLabel-root": { color: "var(--text-primary)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--text-primary)" },
  "& .MuiOutlinedInput-root": {
    color: "var(--text-primary)",
    "& fieldset": { borderColor: "var(--border-color)" },
    "&:hover fieldset": { borderColor: "var(--border-color)" },
    "&.Mui-focused fieldset": { borderColor: "var(--text-primary)" },
    "& input": { color: "var(--text-primary)" },
    "& input::-webkit-calendar-picker-indicator": {
      filter: "invert(1)",
      cursor: "pointer",
    },
  },
};

const menuProps = {
  PaperProps: {
    sx: {
      background: "var(--background-primary)",
      color: "var(--text-primary)",
    },
  },
};

const formatDate = (value) => {
  if (!value || Number.isNaN(new Date(value).getTime())) return "N/A";
  return isoTimestampToReadable(convertToIst(value));
};

const numericValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatAmount = (value, currency = "") => {
  if (value === null || value === undefined || value === "") return "N/A";
  return `${currency ? `${currency} ` : ""}${numericValue(value).toLocaleString(
    "en-IN",
    { maximumFractionDigits: 2 },
  )}`;
};

const AllRechargesData = () => {
  const { allRechargesData, getAllRechargesData } = useAppContext();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const showFilters = Boolean(filterAnchorEl);
  const rows = Array.isArray(allRechargesData) ? allRechargesData : [];

  const currencyOptions = useMemo(
    () => [
      { label: "All currencies", value: "" },
      ...[...new Set(rows.map((row) => String(row.currency || "").trim()))]
        .filter(Boolean)
        .sort()
        .map((currency) => ({ label: currency, value: currency })),
    ],
    [rows],
  );

  const rechargeColumns = [
    { field: "id", headerName: "ID", width: 80, sortable: true },
    {
      field: "rechargeDate",
      headerName: "Recharge Date",
      width: 200,
      valueGetter: (value, row) => formatDate(row?.rechargeDate),
    },
    { field: "corpName", headerName: "Corporate ID", width: 190 },
    {
      field: "refId",
      headerName: "Reference ID",
      width: 190,
      sortable: false,
    },
    { field: "currency", headerName: "Currency", width: 105 },
    {
      field: "amount",
      headerName: "Amount",
      width: 130,
      valueGetter: (value, row) => formatAmount(row?.amount, row?.currency),
    },
    {
      field: "exchangeRate",
      headerName: "Exchange Rate",
      width: 135,
      valueGetter: (value, row) => formatAmount(row?.exchangeRate),
    },
    {
      field: "amountInInr",
      headerName: "Amount (INR)",
      width: 145,
      valueGetter: (value, row) => `₹${formatAmount(row?.amountInInr)}`,
    },
    {
      field: "coinsPurchased",
      headerName: "Coins Purchased",
      width: 155,
      valueGetter: (value, row) =>
        numericValue(row?.coinsPurchased).toLocaleString("en-IN"),
    },
  ];

  const handleApplyFilters = () => {
    if (isInvalidDateRange(filters.dateFrom, filters.dateTo)) {
      return toast.warn(
        "The start date must be before or equal to the end date.",
      );
    }

    const minAmount =
      filters.minAmount === "" ? null : Number(filters.minAmount);
    const maxAmount =
      filters.maxAmount === "" ? null : Number(filters.maxAmount);
    if (
      (minAmount !== null && (!Number.isFinite(minAmount) || minAmount < 0)) ||
      (maxAmount !== null && (!Number.isFinite(maxAmount) || maxAmount < 0)) ||
      (minAmount !== null && maxAmount !== null && minAmount > maxAmount)
    ) {
      return toast.warn("Enter a valid amount range.");
    }

    setAppliedFilters(filters);
    setFilterAnchorEl(null);
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setFilterAnchorEl(null);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await getAllRechargesData();
    } finally {
      setIsRefreshing(false);
    }
  };

  const filteredData = useMemo(() => {
    return rows.filter((row) => {
      const rechargeDate = parseFilterDate(row.rechargeDate);
      if (
        appliedFilters.dateFrom &&
        (!rechargeDate ||
          rechargeDate < parseFilterDate(appliedFilters.dateFrom))
      ) {
        return false;
      }
      if (
        appliedFilters.dateTo &&
        (!rechargeDate ||
          rechargeDate > parseFilterDate(appliedFilters.dateTo, true))
      ) {
        return false;
      }

      if (
        appliedFilters.currency &&
        String(row.currency || "").toLowerCase() !==
          appliedFilters.currency.toLowerCase()
      ) {
        return false;
      }

      if (
        appliedFilters.corpName &&
        !String(row.corpName || "")
          .toLowerCase()
          .includes(appliedFilters.corpName.trim().toLowerCase())
      ) {
        return false;
      }

      const amount = numericValue(row.amount);
      if (
        appliedFilters.minAmount !== "" &&
        amount < Number(appliedFilters.minAmount)
      ) {
        return false;
      }
      if (
        appliedFilters.maxAmount !== "" &&
        amount > Number(appliedFilters.maxAmount)
      ) {
        return false;
      }

      return true;
    });
  }, [rows, appliedFilters]);

  const activeFilterCount = Object.values(appliedFilters).filter(
    (value) => value !== "" && value !== null,
  ).length;

  const summary = useMemo(() => {
    const totalInr = filteredData.reduce(
      (sum, row) => sum + numericValue(row.amountInInr),
      0,
    );
    const totalCoins = filteredData.reduce(
      (sum, row) => sum + numericValue(row.coinsPurchased),
      0,
    );
    return { total: filteredData.length, totalInr, totalCoins };
  }, [filteredData]);

  useEffect(() => {
    getAllRechargesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="space-y-6 p-5">
      <section className="relative overflow-hidden rounded-2xl border border-borderColor bg-backgroundPrimary p-6 shadow-sm lg:p-7">
        <div className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full bg-accent/10 blur-3xl" />
        <div className="relative flex flex-col justify-between gap-5 sm:flex-row sm:items-start">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-accent">
              <MdPayments size={18} /> Revenue operations
            </div>
            <h2>All recharges</h2>
            <p className="mt-2 max-w-2xl text-sm text-textSecondary">
              Review recharge activity from users assigned to your partner
              account and narrow it down by date, currency, account, or amount.
            </p>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-borderColor bg-backgroundSecondary px-4 py-2.5 text-sm font-semibold text-textPrimary transition hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-60"
          >
            <MdRefresh
              className={isRefreshing ? "animate-spin" : ""}
              size={18}
            />
            {isRefreshing ? "Refreshing" : "Refresh"}
          </button>
        </div>

        <div className="relative mt-7 grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="rounded-xl border border-borderColor bg-backgroundSecondary/50 p-4">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-textSecondary">
              <MdAccountBalanceWallet size={16} /> Visible recharges
            </p>
            <p className="mt-2 text-2xl font-bold text-textPrimary">
              {summary.total.toLocaleString("en-IN")}
            </p>
          </div>
          <div className="rounded-xl border border-emerald-200/60 bg-emerald-50/70 p-4 dark:border-emerald-900/50 dark:bg-emerald-950/20">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
              <MdTrendingUp size={16} /> Total value (INR)
            </p>
            <p className="mt-2 text-2xl font-bold text-emerald-800 dark:text-emerald-200">
              ₹
              {summary.totalInr.toLocaleString("en-IN", {
                maximumFractionDigits: 2,
              })}
            </p>
          </div>
          <div className="rounded-xl border border-sky-200/60 bg-sky-50/70 p-4 dark:border-sky-900/50 dark:bg-sky-950/20">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-sky-700 dark:text-sky-300">
              <MdCalendarMonth size={16} /> Coins purchased
            </p>
            <p className="mt-2 text-2xl font-bold text-sky-800 dark:text-sky-200">
              {summary.totalCoins.toLocaleString("en-IN")}
            </p>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-borderColor bg-backgroundPrimary shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b border-borderColor p-4 lg:p-5">
          <Tooltip title="Filter recharge data">
            <button
              type="button"
              onClick={(event) => setFilterAnchorEl(event.currentTarget)}
              className="flex items-center gap-1 rounded border border-borderColor bg-accent px-3 py-1.5 text-sm font-medium text-white transition hover:opacity-90"
            >
              <MdFilterList size={18} /> Filters
              {activeFilterCount > 0 && (
                <span className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold text-accent">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </Tooltip>

          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={handleResetFilters}
              className="flex items-center gap-1 rounded border border-red-300 px-3 py-1.5 text-sm font-medium text-red-600 transition hover:bg-red-50"
            >
              <MdClear size={16} /> Reset all
            </button>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {Object.entries(appliedFilters).map(([key, value]) =>
              value === "" || value === null ? null : (
                <Chip
                  key={key}
                  label={`${camelCaseToCapitalCase(key)}: ${value}`}
                  onDelete={() => {
                    const next = {
                      ...appliedFilters,
                      [key]: DEFAULT_FILTERS[key],
                    };
                    setAppliedFilters(next);
                    setFilters(next);
                  }}
                  variant="outlined"
                  sx={{
                    borderColor: "var(--border-color)",
                    color: "var(--text-secondary)",
                    "& .MuiChip-deleteIcon": { color: "var(--text-primary)" },
                  }}
                />
              ),
            )}
          </div>

          <span className="ml-auto text-sm text-textSecondary">
            Showing <strong>{filteredData.length}</strong> of{" "}
            <strong>{rows.length}</strong> recharges
          </span>
        </div>

        <Dialog
          open={showFilters}
          onClose={() => setFilterAnchorEl(null)}
          PaperProps={{
            sx: {
              background: "var(--background-primary)",
              borderRadius: 2,
              maxWidth: "620px",
              width: "100%",
            },
          }}
        >
          <div className="rounded-lg bg-backgroundPrimary p-6 text-textPrimary">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-accent">
                  Refine results
                </p>
                <h3 className="mt-1 text-xl font-bold">Recharge filters</h3>
              </div>
              <button
                type="button"
                onClick={() => setFilterAnchorEl(null)}
                className="text-xl text-textSecondary hover:text-textPrimary"
                aria-label="Close filters"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField
                size="small"
                type="date"
                label="Recharge date from"
                InputLabelProps={{ shrink: true }}
                value={filters.dateFrom}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    dateFrom: event.target.value,
                  }))
                }
                sx={filterInputSx}
              />
              <TextField
                size="small"
                type="date"
                label="Recharge date to"
                InputLabelProps={{ shrink: true }}
                value={filters.dateTo}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    dateTo: event.target.value,
                  }))
                }
                sx={filterInputSx}
              />
              <FormControl size="small" fullWidth sx={filterInputSx}>
                <InputLabel>Currency</InputLabel>
                <Select
                  label="Currency"
                  value={filters.currency}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      currency: event.target.value,
                    }))
                  }
                  MenuProps={menuProps}
                >
                  {currencyOptions.map((option) => (
                    <MenuItem key={option.value || "all"} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Corporate ID"
                placeholder="Search corporate ID"
                value={filters.corpName}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    corpName: event.target.value,
                  }))
                }
                sx={filterInputSx}
              />
              <TextField
                size="small"
                type="number"
                label="Minimum amount"
                inputProps={{ min: 0, step: "any" }}
                value={filters.minAmount}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    minAmount: event.target.value,
                  }))
                }
                sx={filterInputSx}
              />
              <TextField
                size="small"
                type="number"
                label="Maximum amount"
                inputProps={{ min: 0, step: "any" }}
                value={filters.maxAmount}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    maxAmount: event.target.value,
                  }))
                }
                sx={filterInputSx}
              />
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="outlined"
                color="error"
                onClick={handleResetFilters}
              >
                Reset
              </Button>
              <Button variant="contained" onClick={handleApplyFilters}>
                Apply filters
              </Button>
            </div>
          </div>
        </Dialog>

        <div className="p-3 lg:p-5">
          <CustomTable
            rows={filteredData}
            columns={rechargeColumns}
            pageSize={50}
            pageSizeOptions={[50, 100, 250]}
            searchable={true}
            searchPlaceholder="Search by corporate ID, reference, currency…"
            emptyMessage={
              rows.length
                ? "No recharges match the current filters."
                : "No recharge data available."
            }
            exportName="partner-recharges"
            isExport={true}
          />
        </div>
      </section>
    </main>
  );
};

export default AllRechargesData;
